import { motion } from "framer-motion";
import { ArrowUpRight, Globe, Workflow, Megaphone } from "lucide-react";
import { scrollToHash } from "@/components/Navbar";
import { useLanguage } from "@/lib/i18n";

const meta = [
  { id: "website-creation", icon: Globe, span: "md:col-span-7 md:row-span-2" },
  { id: "automations", icon: Workflow, span: "md:col-span-5" },
  { id: "social-media", icon: Megaphone, span: "md:col-span-5" },
];

export default function Services() {
  const { t } = useLanguage();
  const items = t("services.items");
  const services = meta.map((m, i) => ({ ...m, ...items[i] }));
  return (
    <section id="services" className="py-32" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] mb-4" data-testid="services-eyebrow">
            {t("services.eyebrow")}
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none max-w-2xl" data-testid="services-heading">
            {t("services.heading")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {services.map((s, i) => (
            <motion.a
              key={s.id}
              href="#contact"
              onClick={scrollToHash("#contact")}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative border border-white/10 p-8 md:p-10 flex flex-col justify-between min-h-[280px] hover:border-[#FF5A00]/60 hover:bg-[#FF5A00]/[0.04] transition-colors duration-500 ${s.span}`}
              data-testid={`service-card-${s.id}`}
            >
              <div>
                <div className="flex items-start justify-between">
                  <s.icon className="w-8 h-8 text-[#FFB800]" strokeWidth={1.5} />
                  <span className="font-syne text-sm text-neutral-600">0{i + 1}</span>
                </div>
                <h3 className="font-syne font-bold text-2xl md:text-3xl mt-8 group-hover:text-[#FF5A00] transition-colors duration-500">
                  {s.title}
                </h3>
                <p className="text-neutral-400 mt-4 leading-relaxed max-w-md">{s.description}</p>
              </div>
              <div className="mt-10 flex items-end justify-between gap-4">
                <ul className="flex flex-wrap gap-2">
                  {s.deliverables.map((d) => (
                    <li
                      key={d}
                      className="text-xs text-neutral-400 border border-white/10 px-3 py-1.5 rounded-full"
                    >
                      {d}
                    </li>
                  ))}
                </ul>
                <ArrowUpRight className="w-6 h-6 shrink-0 text-neutral-600 group-hover:text-[#FF5A00] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
